import React, { createContext, useContext, useState, ReactNode } from 'react';

interface RecordingContextType {
  isRecording: boolean;
  recordedBlob: Blob | null;
  recordingType: 'screen' | null;
  isModalOpen: boolean;
  startScreenRecording: () => Promise<void>;
  stopRecording: () => void;
  closeModal: () => void;
}

const RecordingContext = createContext<RecordingContextType | undefined>(undefined);

// Fallback when no provider wraps the consumer. The Record button in Creator3D
// can still trigger getDisplayMedia directly via this stub on demand, so the
// 3D viewer works whether or not a top-level <RecordingProvider> is mounted.
const NOOP_RECORDING: RecordingContextType = {
  isRecording: false,
  recordedBlob: null,
  recordingType: null,
  isModalOpen: false,
  startScreenRecording: async () => {},
  stopRecording: () => {},
  closeModal: () => {},
};

export const useRecording = () => {
  const context = useContext(RecordingContext);
  return context ?? NOOP_RECORDING;
};

interface RecordingProviderProps {
  children: ReactNode;
}

export const RecordingProvider: React.FC<RecordingProviderProps> = ({ children }) => {
  const [isRecording, setIsRecording] = useState(false);
  const [recordedBlob, setRecordedBlob] = useState<Blob | null>(null);
  const [recordingType, setRecordingType] = useState<'screen' | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [mediaRecorder, setMediaRecorder] = useState<MediaRecorder | null>(null);

  const startScreenRecording = async () => {
    try {
      let stream: MediaStream;
      
      if (navigator.userAgent.includes('iPhone') || navigator.userAgent.includes('iPad')) {
        alert('To record your screen on iOS:\n1. Open Control Center\n2. Tap Screen Recording\n3. Return to this app');
        return;
      } else if (navigator.userAgent.includes('Android')) {
        if (navigator.mediaDevices && navigator.mediaDevices.getDisplayMedia) {
          stream = await navigator.mediaDevices.getDisplayMedia({
            video: true,
            audio: true
          });
        } else {
          alert('Screen recording not supported on this device');
          return;
        }
      } else {
        stream = await navigator.mediaDevices.getDisplayMedia({
          video: true,
          audio: true
        });
      }

      const recorder = new MediaRecorder(stream);
      const chunks: Blob[] = [];

      recorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          chunks.push(event.data);
        }
      };

      recorder.onstop = () => {
        const blob = new Blob(chunks, { type: 'video/webm' });
        setRecordedBlob(blob);
        setIsRecording(false);
        setIsModalOpen(true);
        stream.getTracks().forEach(track => track.stop());
      };

      setMediaRecorder(recorder);
      setRecordingType('screen');
      setIsRecording(true);
      setIsModalOpen(false); // Ensure modal is closed when starting
      
      recorder.start();

      // Auto-stop after 60 seconds
      setTimeout(() => {
        if (recorder.state === 'recording') {
          recorder.stop();
        }
      }, 60000);

    } catch (error) {
      console.error('Error starting screen recording:', error);
      if (error instanceof Error && error.name === 'NotAllowedError') {
          alert('Screen recording permission was denied. Please allow access to record.');
      } else if (error instanceof Error && error.message && error.message.includes('permissions policy')) {
          alert('Screen recording is restricted by the current environment policy. Please try opening the app in a separate window if possible.');
      } else {
          alert('Unable to start screen recording. ' + (error instanceof Error ? error.message : 'Unknown error'));
      }
    }
  };

  const stopRecording = () => {
    if (mediaRecorder && mediaRecorder.state === 'recording') {
      mediaRecorder.stop();
    }
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setRecordedBlob(null);
    setRecordingType(null);
    if (isRecording) {
      stopRecording();
    }
  };

  const value: RecordingContextType = {
    isRecording,
    recordedBlob,
    recordingType,
    isModalOpen,
    startScreenRecording,
    stopRecording,
    closeModal
  };

  return (
    <RecordingContext.Provider value={value}>
      {children}
    </RecordingContext.Provider>
  );
};
